import Box from '@/common/components/Box'
import BoxWithHeader from '@/common/components/BoxWithHeader'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ResumeItem from '@/common/components/ResumeItem'
import { ShowHeader } from '@/common/components/ShowHeader'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ForwardingTabContext } from '../../../../hooks/forwardings/useForwardingTabContext'
import { useMaintenanceForwarding } from '../../../../hooks/forwardings/useMaintenanceForwarding'
import ExecutionObjectiveItem from '../../../../pages/Show/components/tabs/ForwardingsTab/sections/DirectiveEntry/entries/ExecutionEntry/content/ExecutionEntryExecution/ExecutionObjectiveItem'
import { MaintenanceForwardingDirectiveEntryStatus } from '../../../../types/forwardings/maintenanceForwarding.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '../../../../types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type'
import MaintenanceWorkflowExecutionEntryExecutionLoading from './components/MaintenanceWorkflowExecutionEntryExecutionLoading'
import AdvanceExecutionEntryModal from './components/AdvanceExecutionEntryModal'

const MaintenanceWorkflowExecutionEntryExecution = () => {
  const { workflowId, forwardingId } = useParams()

  const { isUserPermissionsPending } = useUserPermissions()

  const navigate = useNavigate()

  const {
    data: forwarding,
    isPending: forwardingIsPending,
    error: forwardingError = {} as any,
    refetch: refetchForwarding,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding<MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry>(forwardingId)

  const isLoading = useMemo(() => {
    return isUserPermissionsPending || forwardingIsPending
  }, [isUserPermissionsPending, forwardingIsPending])

  const isInProgress = useMemo(() => {
    return forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.InProgress
  }, [forwardingUpdatedAt])

  useEffect(() => {
    if (forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.AwaitingClientReview) {
      navigate(`/maintenance-workflows/${workflowId}/Maintenance::Directive::ExecutionEntry`, { replace: true })
    }
  }, [forwardingUpdatedAt])

  return (
    <div className="inner-screen">
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/maintenance-workflows/${workflowId}/Maintenance::Directive::ExecutionEntry`,
          searchLastRouteFromFallback: true,
        }}
        isLoading={isLoading}
        title="Execução da Manutenção"
        titleClassName="text-lg font-medium"
        buttons={
          <AdvanceExecutionEntryModal
            forwarding={forwarding}
            forwardingUpdatedAt={forwardingUpdatedAt}
          />
        }
      />

      {isLoading && <MaintenanceWorkflowExecutionEntryExecutionLoading />}

      {!isLoading && forwarding && (
        <ErrorMessageWrapper
          className="flex-1"
          statusCode={forwardingError?.status}
          refetchFunction={refetchForwarding}
        >
          <ForwardingTabContext.Provider
            value={{
              forwarding,
              forwardingUpdatedAt,
              directiveEntryKlass: MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry,
            }}
          >
            <div className="flex-1 flex gap-6 overflow-hidden">
              <div className="flex-1 overflow-auto">
                <Box className="p-4 flex flex-col gap-4">
                  <div className="flex w-full flex-col gap-y-4">
                    <div className="flex flex-col gap-3">
                      <ResumeItem
                        label="Parecer técnico"
                        value={forwarding.directive_entry?.diagnostic?.technical_response || '--'}
                        className="text-xs"
                        labelClassName="text-sm"
                        valueClassName="font-bold text-sm"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1.5">
                        {forwarding?.directive_entry?.objectives?.map((objective) => (
                          <ExecutionObjectiveItem
                            key={objective.id}
                            objective={objective}
                            isInProgress={isInProgress}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </Box>
              </div>

              <div>
                <BoxWithHeader
                  title="Checklist"
                  subtitle={forwarding?.directive_entry?.requirement_list_summary?.description}
                >
                  <div className="flex flex-col gap-2">
                    {forwarding?.directive_entry?.requirement_list_summary?.steps?.map((step) => (
                      <Checkbox
                        key={step.name}
                        label={step.name}
                        name={step.name.replace(/\s/g, '_').toLowerCase()}
                        checked={step.passed}
                        disabled
                        labelClassName="italic text-xs"
                      />
                    ))}
                  </div>

                  <AdvanceExecutionEntryModal
                    forwarding={forwarding}
                    forwardingUpdatedAt={forwardingUpdatedAt}
                  />
                </BoxWithHeader>
              </div>
            </div>
          </ForwardingTabContext.Provider>
        </ErrorMessageWrapper>
      )}
    </div>
  )
}

export default MaintenanceWorkflowExecutionEntryExecution
